<template>
  <ExpandableCard v-model:expanded="isExpanded" class="selected-rate-card">
    <div class="selected-rate-card-details">
      <RateQuoteCarrier
        :rate-quote="rateQuote"
        with-context-chat
        class="selected-rate-card-carrier w-4"
      />

      <div
        class="selected-rate-card-highlights flex-1 flex flex-gap-1 justify-content-between"
      >
        <KeyValuePair
          class="selected-rate-card-rate"
          :class="{ 'has-rate': rateQuote.totalPrice !== undefined }"
          :label="t('rateShop.rates')"
          :value="
            renderMoney(rateQuote.totalPrice, {
              currencyCode: rateQuote.currencyCode,
            })
          "
        />

        <KeyValuePair
          v-if="!isInBrokerage"
          :label="t('rateShop.service')"
          :value="titleCase(rateQuote.serviceType)"
          class="selected-rate-card-rate-service-type"
        />

        <KeyValuePair
          v-if="!isInBrokerage"
          :label="t('rateShop.transit')"
          :value="
            rateQuote.transitTime
              ? t('rateShop.countDays', { count: rateQuote.transitTime })
              : '-'
          "
          class="selected-rate-card-rate-transit-time"
        />

        <KeyValuePair
          :label="t('rateShop.rateSource')"
          class="selected-rate-card-rate-source"
        >
          <template #value>
            <RateShopTableSource
              :rate-quote="rateQuote"
              :can-edit-rate="editable"
              :disabled="loading"
              @edit-brokerage-quote="emit('edit-brokerage-quote', $event)"
              @edit-spot-quote="emit('edit-spot-quote', $event)"
              @edit-quote="emit('edit-quote', $event)"
            />
          </template>
        </KeyValuePair>
      </div>
    </div>

    <RateQuoteWarnings :rate-quote="rateQuote" @click="onChipClicked" />

    <template #expansion>
      <RateQuoteExpandedDetails
        v-model:active-tab-key="activeTabKey"
        :rate-quote="rateQuote"
        :email-tender="isEmailTender"
        class="w-full"
      />
    </template>
  </ExpandableCard>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney, titleCase } from '@rygen/rygen-ui/i18n';
import {
  CapacityProviderType,
  CarrierSetting,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { RateQuote } from '@/model';

import RateQuoteExpandedDetails, {
  TabKey,
} from '@/components/rateShop/expandedDetails/RateQuoteExpandedDetails.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RateQuoteWarnings from '@/components/rateShop/RateQuoteWarnings.vue';
import RateShopTableSource from '@/components/rateShop/RateShopTableSource.vue';
import ExpandableCard from '@rygen/rygen-ui/components/ui/ExpandableCard.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  rateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
  brokerSetting: {
    type: Object as PropType<CarrierSetting | undefined>,
    default: () => undefined,
  },
  /**
   * Specifies if this instance of this component allows editing the rate
   *
   * Editability rules (permissions, rate source, parent entity status, etc) are
   * handled in `RateShopTableSource`
   */
  editable: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const { rateQuote, brokerSetting, editable, loading } = toRefs(props);
const emit = defineEmits<{
  (event: 'edit-brokerage-quote', spotQuote: RateQuote): void;
  (event: 'edit-spot-quote', spotQuote: RateQuote): void;
  (event: 'edit-quote', quote: RateQuote): void;
}>();

const { t } = useI18n();

const isExpanded = ref(false);
const activeTabKey: Ref<TabKey | undefined> = ref();

const isInBrokerage = computed(
  () =>
    rateQuote.value.source === 'BROKERAGE' ||
    rateQuote.value.brokeredCarrier?.type === CapacityProviderType.CARRIER,
);

const isEmailTender = computed(() => {
  if (!isInBrokerage.value) {
    return false;
  }

  return brokerSetting?.value?.tenderConfig?.some(
    (t) => t.method === TenderMethod.EMAIL,
  );
});

const onChipClicked = () => {
  isExpanded.value = true;
  nextTick(() => (activeTabKey.value = 'OTHER_DETAILS'));
};

watch(isExpanded, (newVal: boolean) => {
  if (!newVal) {
    activeTabKey.value = 'CHARGES';
  }
});
</script>

<style>
.selected-rate-card.p-card {
  .expandable-card-content {
    .selected-rate-card-details {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .selected-rate-card-carrier {
        border-right: 1px solid var(--divider-color);
        padding: 0 var(--sp-6) 0 0;
        align-items: center;

        .p-avatar {
          flex: 0 0 auto;
          width: 72px;
          height: 72px;
        }

        .rate-quote-carrier-name {
          font-size: 16px;
        }
      }

      .selected-rate-card-highlights {
        padding: 0 0 0 var(--sp-6);

        .selected-rate-card-rate {
          &.has-rate {
            .key-value-pair-value {
              color: var(--success-color);
            }
          }
        }

        .selected-rate-card-rate-source {
          &:has(.rygen-button) {
            .key-value-pair-label {
              /**
              Add a left margin so label lines up with button text:
              button padding-left + icon width + icon margin-right + 1px for some reason
              */
              margin-left: calc(
                var(--p-button-sm-padding-x) +
                  var(--p-form-field-sm-font-size) + var(--p-button-gap) + 1px
              );
            }
          }
        }
      }
    }

    .rate-quote-warnings {
      margin-top: var(--sp-3);
    }
  }

  .expandable-card-expansion {
    padding-top: var(--sp-3);
  }
}
</style>
